import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, TextField, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  List, ListItem, ListItemText, ListItemSecondaryAction, Divider, Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';

const EMPTY_AGENT = {
  name: '', description: '', system_prompt: '', model: 'gpt-5.5',
  icon: '', color: '#526075', category: '', profile_image_url: '',
};

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function AgentManager({ agents, onRefresh, userEmail }) {
  const [formOpen, setFormOpen] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [form, setForm] = useState(EMPTY_AGENT);
  const [categoryInput, setCategoryInput] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const categories = form.category ? form.category.split(',').map((c) => c.trim()).filter(Boolean) : [];

  const openCreate = () => {
    setEditAgent(null);
    setForm(EMPTY_AGENT);
    setCategoryInput('');
    setFiles([]);
    setFormOpen(true);
  };

  const openEdit = (agent) => {
    setEditAgent(agent);
    setForm({
      name: agent.name,
      description: agent.description || '',
      system_prompt: agent.system_prompt,
      model: agent.model || 'gpt-5.5',
      icon: agent.icon || '',
      color: agent.color || '#526075',
      category: agent.category || '',
      profile_image_url: agent.profile_image_url || '',
    });
    setCategoryInput('');
    setFormOpen(true);
    fetchFiles(agent.id);
  };

  const fetchFiles = async (agentId) => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/agents/${agentId}/files`);
      if (res.ok) setFiles(await res.json());
    } catch (err) {
      console.error('[AgentManager] Failed to fetch files:', err);
    }
  };

  const addCategory = () => {
    const val = categoryInput.trim();
    if (!val) return;
    if (categories.includes(val)) { setCategoryInput(''); return; }
    const newCats = [...categories, val].join(', ');
    setForm({ ...form, category: newCats });
    setCategoryInput('');
  };

  const removeCategory = (cat) => {
    const newCats = categories.filter((c) => c !== cat).join(', ');
    setForm({ ...form, category: newCats });
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.system_prompt.trim()) {
      toast.error('Naam en system prompt zijn verplicht');
      return;
    }
    try {
      const url = editAgent
        ? `${AI_ENGINE_URL}/api/v1/chat/agents/${editAgent.id}`
        : `${AI_ENGINE_URL}/api/v1/chat/agents`;
      const method = editAgent ? 'PUT' : 'POST';
      const body = editAgent ? form : { ...form, created_by: userEmail };

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Opslaan mislukt');
      toast.success(editAgent ? 'Agent bijgewerkt' : 'Agent aangemaakt');
      setFormOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (agent) => {
    if (!(await bevestig({ titel: 'Agent verwijderen?', tekst: `Agent "${agent.name}" wordt definitief verwijderd, inclusief bestanden.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/agents/${agent.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      toast.success('Agent verwijderd');
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFileUpload = async (e) => {
    if (!editAgent) {
      toast.error('Sla de agent eerst op voordat je bestanden uploadt');
      return;
    }
    const uploadFiles = Array.from(e.target.files);
    if (!uploadFiles.length) return;

    setUploading(true);
    for (const file of uploadFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/agents/${editAgent.id}/files`, {
          method: 'POST',
          body: formData,
        });
        if (!res.ok) throw new Error(`Upload mislukt: ${file.name}`);
      } catch (err) {
        toast.error(err.message);
      }
    }
    setUploading(false);
    fetchFiles(editAgent.id);
    toast.success('Bestanden geüpload');
    e.target.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    if (!(await bevestig({ titel: 'Bestand verwijderen?', tekst: 'Het bestand wordt uit de kennis van deze agent verwijderd. Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/agents/${editAgent.id}/files/${fileId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success('Bestand verwijderd');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Agents</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontSize: 13 }}>
          Nieuw
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {agents.map((agent) => (
          <Card key={agent.id} variant="outlined" sx={{ p: 1.5, borderColor: 'rgba(45,51,53,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {agent.profile_image_url ? (
                <Avatar src={agent.profile_image_url} sx={{ width: 24, height: 24 }} />
              ) : (
                <SmartToyIcon sx={{ color: agent.color || '#526075', fontSize: 20 }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontSize={13} fontWeight={600} noWrap>{agent.name}</Typography>
                <Typography fontSize={11} color="text.secondary" noWrap>{agent.description}</Typography>
                {agent.category && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {agent.category.split(',').map((c) => c.trim()).filter(Boolean).map((cat) => (
                      <Chip key={cat} label={cat} size="small" sx={{ fontSize: 9, height: 16 }} />
                    ))}
                  </Box>
                )}
              </Box>
              <IconButton size="small" onClick={() => openEdit(agent)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
              <IconButton size="small" onClick={() => handleDelete(agent)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
            </Box>
          </Card>
        ))}
        {agents.length === 0 && (
          <Typography sx={{ py: 3, textAlign: 'center', color: '#adb3b5', fontSize: 13 }}>
            Nog geen agents. Maak er een aan.
          </Typography>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editAgent ? 'Agent bewerken' : 'Nieuwe Agent'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {/* General Information */}
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mt: 0.5 }}>
            Algemene informatie
          </Typography>
          <TextField
            label="Naam"
            size="small"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Beschrijving"
            size="small"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
          />
          <TextField
            label="Profielfoto URL"
            size="small"
            value={form.profile_image_url}
            onChange={(e) => setForm({ ...form, profile_image_url: e.target.value })}
            fullWidth
            placeholder="https://..."
          />

          {/* Categories */}
          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Categorie toevoegen"
                size="small"
                value={categoryInput}
                onChange={(e) => setCategoryInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                sx={{ flex: 1 }}
              />
              <Button size="small" onClick={addCategory} sx={{ textTransform: 'none', minWidth: 'auto' }}>+</Button>
            </Box>
            {categories.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                {categories.map((cat) => (
                  <Chip key={cat} label={cat} size="small" onDelete={() => removeCategory(cat)} />
                ))}
              </Box>
            )}
          </Box>

          <Divider />

          {/* System Instruction */}
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            System instructie
          </Typography>
          <TextField
            label="System Prompt"
            multiline
            minRows={4}
            maxRows={10}
            value={form.system_prompt}
            onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
            fullWidth
            placeholder="Je bent een behulpzame AI assistent die..."
            helperText={`${form.system_prompt.length} tekens`}
          />

          {/* Model */}
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Model
          </Typography>
          <Select
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
            size="small"
            fullWidth
          >
            <MenuItem value="gpt-5.5">GPT-5.5</MenuItem>
            <MenuItem value="gpt-5.4">GPT-5.4</MenuItem>
            <MenuItem value="gpt-5.4-mini">GPT-5.4 Mini</MenuItem>
            <MenuItem value="gpt-5.4-pro">GPT-5.4 Pro</MenuItem>
            <MenuItem value="gpt-5.4-nano">GPT-5.4 Nano</MenuItem>
          </Select>

          {/* Training Files - only in edit mode */}
          {editAgent && (
            <>
              <Divider />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  Training bestanden
                </Typography>
                <Button
                  size="small"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={uploading}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  {uploading ? 'Uploading...' : 'Selecteer bestanden'}
                  <input type="file" hidden multiple accept=".txt,.md,.pdf,.csv,.json,.doc,.docx" onChange={handleFileUpload} />
                </Button>
              </Box>
              {files.length > 0 ? (
                <List dense sx={{ bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid rgba(45,51,53,0.08)' }}>
                  {files.map((f) => (
                    <ListItem key={f.id}>
                      <ListItemText
                        primary={f.original_filename}
                        secondary={formatFileSize(f.file_size)}
                        primaryTypographyProps={{ fontSize: 13 }}
                        secondaryTypographyProps={{ fontSize: 11 }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton size="small" onClick={() => handleDeleteFile(f.id)}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography fontSize={12} color="text.secondary">
                  Geen training bestanden. Upload documenten die als context voor deze agent dienen.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Annuleren</Button>
          <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#526075', '&:hover': { bgcolor: '#465469' } }}>
            {editAgent ? 'Opslaan' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
